import { useEffect, useState, RefObject } from 'react';

/**
 * Hook that checks if text content overflows its container
 * @param ref Reference to the DOM element to check for overflow
 * @returns Boolean indicating if the element is overflowing
 */
export const useTextOverflow = <T extends HTMLElement>(
  ref: RefObject<T>
): boolean => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (ref.current) {
        setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };

    // Check on initial render
    checkOverflow();

    // Create a ResizeObserver to check when the element size changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    // Also listen for window resize events
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [ref]);

  return isOverflowing;
};
